// src/app/modules/Events/pages/EventsOverview.jsx
import React, { useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Ticket, DollarSign, TrendingUp, Plus, ChevronRight } from 'lucide-react';
import * as actions from '../../Dashbord/_redux/dashboard/Actions';
import './events.scss';

export function EventsOverview() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { org } = useSelector(s => ({ org: s.auth.organization }), shallowEqual);
  const { events, eventStats } = useSelector(s => ({
    events:     s.dashboard.events,
    eventStats: s.dashboard.eventStats,
  }), shallowEqual);

  useEffect(() => {
    if (!org) return;
    dispatch(actions.fetchAllData('event_stats', org.organization_id));
    dispatch(actions.fetchAllData('events', org.organization_id, { limit: 5 }));
  }, [org]);

  const kpis = [
    { label: 'Total Events',   value: eventStats?.total_events   ?? '—', icon: CalendarDays, color: '#5341C4', bg: '#F0EEFF' },
    { label: 'Active Events',  value: eventStats?.active_events  ?? '—', icon: TrendingUp,   color: '#16A34A', bg: '#DCFCE7' },
    { label: 'Tickets Sold',   value: eventStats?.tickets_sold   ?? '—', icon: Ticket,       color: '#2563EB', bg: '#DBEAFE' },
    { label: 'Total Revenue',  value: eventStats?.total_revenue  ? `₦${Number(eventStats.total_revenue).toLocaleString()}` : '—', icon: DollarSign, color: '#D97706', bg: '#FEF3C7' },
  ];

  const statusColor = { draft: '#94A3B8', published: '#16A34A', cancelled: '#DC2626', ended: '#6B7280' };
  const statusBg    = { draft: '#F1F5F9', published: '#DCFCE7', cancelled: '#FEE2E2', ended: '#F3F4F6' };

  return (
    <div className="ev-page">
      <div className="ev-page__header">
        <div>
          <h1 className="ev-page__title">Overview</h1>
          <p className="ev-page__sub">Your events at a glance</p>
        </div>
        <button className="ev-btn ev-btn--primary" onClick={() => navigate('/events/new')}>
          <Plus size={15} strokeWidth={2} /> Create Event
        </button>
      </div>

      {/* KPIs */}
      <div className="ev-kpi-grid">
        {kpis.map((k, i) => {
          const Ic = k.icon;
          return (
            <div className="ev-kpi" key={i}>
              <div className="ev-kpi__icon" style={{ background: k.bg, color: k.color }}>
                <Ic size={18} strokeWidth={1.8} />
              </div>
              <div className="ev-kpi__body">
                <div className="ev-kpi__val">{k.value}</div>
                <div className="ev-kpi__label">{k.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent events */}
      <div className="ev-section">
        <div className="ev-section__head">
          <span className="ev-section__title">Recent Events</span>
          <button className="ev-link" onClick={() => navigate('/events/list')}>
            View all <ChevronRight size={13} />
          </button>
        </div>
        <div className="ev-list">
          {(!events || events.length === 0) && (
            <div className="ev-empty">
              <CalendarDays size={32} strokeWidth={1.4} />
              <p>No events yet. Create your first event.</p>
              <button className="ev-btn ev-btn--primary" onClick={() => navigate('/events/new')}>
                <Plus size={14} /> Create Event
              </button>
            </div>
          )}
          {events?.map(ev => (
            <div className="ev-row" key={ev.event_id} onClick={() => navigate(`/events/${ev.event_id}`)}>
              <div className="ev-row__info">
                <span className="ev-row__name">{ev.name}</span>
                <span className="ev-row__date">{ev.start_date ? new Date(ev.start_date).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</span>
              </div>
              <div className="ev-row__right">
                <span className="ev-badge" style={{ background: statusBg[ev.status] || '#F1F5F9', color: statusColor[ev.status] || '#52546A' }}>
                  {ev.status || 'draft'}
                </span>
                <span className="ev-row__tickets">{ev.tickets_sold ?? 0} sold</span>
                <ChevronRight size={14} strokeWidth={1.8} color="#CACCD8" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
